import React from 'react';
import { Card, Descriptions, Typography, Space, Tag, Alert, Table, Empty } from 'antd';
import { 
  TeamOutlined,
  UserOutlined,
  ShoppingOutlined,
  EnvironmentOutlined,
  FileOutlined,
  CheckCircleOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';

const { Title, Text, Paragraph } = Typography;

interface GroupReviewFormProps {
  categoryId: number;
  categoryProducts?: any[];
  categoryLocations?: any[];
  categoryDocuments?: any[];
}

const GroupReviewForm: React.FC<GroupReviewFormProps> = ({
  categoryId,
  categoryProducts = [],
  categoryLocations = [],
  categoryDocuments = [],
}) => {
  const { formData } = useOnboardingStore();

  // Group member columns for table
  const memberColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (text: string, record: any) => {
        const data = record.data || {};
        const memberName = data.fullName || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : data.name) || 'N/A';
        return (
          <Space>
            {record.isLeader && <CrownOutlined style={{ color: '#faad14' }} />}
            <Text strong={record.isLeader}>{memberName}</Text>
            {record.isLeader && <Tag color="gold">Leader</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'ID Number',
      key: 'idCard',
      render: (text: string, record: any) => record.data?.idCard || 'N/A',
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (text: string, record: any) => record.data?.phone || 'N/A',
    },
    {
      title: 'Gender',
      key: 'custSex',
      render: (text: string, record: any) => record.data?.custSex || 'N/A',
    },
  ];

  return (
    <div className="group-review-form" style={{ padding: '24px 0' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Title level={3}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              Review & Submit
            </Title>
            <Paragraph type="secondary">
              Please review all the information below before submitting. You can go back to any step to make changes.
            </Paragraph>
          </Space>
        </Card>

        {/* Group Information */}
        {formData.customerData && Object.keys(formData.customerData).length > 0 && (
          <Card 
            title={
              <Space>
                <TeamOutlined />
                <span>Group Information</span>
              </Space>
            }
            size="small"
          >
            <Descriptions bordered column={2} size="small">
              {formData.customerData.groupName && (
                <Descriptions.Item label="Group Name">
                  <Text>{String(formData.customerData.groupName)}</Text>
                </Descriptions.Item>
              )}
              {formData.customerData.groupCode && (
                <Descriptions.Item label="Group Code">
                  <Tag color="blue">{String(formData.customerData.groupCode)}</Tag>
                </Descriptions.Item>
              )}
              {formData.customerData.registrationDate && (
                <Descriptions.Item label="Registration Date">
                  <Text>{new Date(formData.customerData.registrationDate).toLocaleDateString()}</Text>
                </Descriptions.Item>
              )}
              {formData.customerData.description && (
                <Descriptions.Item label="Description" span={2}>
                  <Text>{String(formData.customerData.description)}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}

        {/* Group Members */}
        {formData.groupMembers && (
          <Card 
            title={
              <Space>
                <UserOutlined />
                <span>Group Members ({formData.groupMembers.length})</span>
              </Space>
            }
            size="small"
          >
            {formData.groupMembers.length > 0 ? (
              <Table
                columns={memberColumns}
                dataSource={formData.groupMembers}
                rowKey={(record, index) => `member-${index}`}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty
                description="No members added yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        )}

        {/* Products */}
        {formData.productData && formData.productData.length > 0 && (
          <Card 
            title={
              <Space>
                <ShoppingOutlined />
                <span>Selected Products</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {formData.productData.map((product: any, index: number) => (
                <Card key={index} size="small" type="inner">
                  <Descriptions column={2} size="small">
                    {product.productCode && (
                      <Descriptions.Item label="Product Code">
                        <Tag color="blue">{String(product.productCode)}</Tag>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Product Name">
                      <Text>{String(product.productName || 'N/A')}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Product Type">
                      <Text>{String(product.productType || 'N/A')}</Text>
                    </Descriptions.Item>
                    {product.interestRate && (
                      <Descriptions.Item label="Interest Rate">
                        <Text>{String(product.interestRate)}%</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Locations */}
        {formData.locationData && formData.locationData.length > 0 && (
          <Card 
            title={
              <Space>
                <EnvironmentOutlined />
                <span>Address Information</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {formData.locationData.map((location: any, index: number) => (
                <Card key={index} size="small" type="inner">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Address Type">
                      <Tag color={location.isPrimary ? 'green' : 'default'}>
                        {String(location.addressType || 'PHYSICAL')}
                        {location.isPrimary && ' (Primary)'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Configuration">
                      <Text>{String(location.locationConfigName || 'N/A')}</Text>
                    </Descriptions.Item>
                    {location.locationSelections && typeof location.locationSelections === 'object' && Object.keys(location.locationSelections).length > 0 && (
                      <Descriptions.Item label="Location" span={2}>
                        <Text>
                          {Object.entries(location.locationSelections)
                            .map(([key, value]: [string, any]) => {
                              if (value && typeof value === 'object' && 'name' in value) {
                                return `${key}: ${value.name}`;
                              }
                              if (value !== null && value !== undefined) {
                                return `${key}: ${String(value)}`;
                              }
                              return null;
                            })
                            .filter(item => item !== null)
                            .join(', ')}
                        </Text>
                      </Descriptions.Item>
                    )}
                    {location.houseNumber && (
                      <Descriptions.Item label="House Number">
                        <Text>{String(location.houseNumber)}</Text>
                      </Descriptions.Item>
                    )}
                    {location.postalCode && (
                      <Descriptions.Item label="Postal Code">
                        <Text>{String(location.postalCode)}</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Documents */}
        {formData.documentData && formData.documentData.length > 0 && (
          <Card 
            title={
              <Space>
                <FileOutlined />
                <span>Uploaded Documents</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {formData.documentData.map((document: any, index: number) => (
                <Card key={index} size="small" type="inner">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Document Type">
                      <Tag color="blue">{String(document.documentTypeCode || 'N/A')}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Document Name">
                      <Text>{String(document.documentTypeName || 'N/A')}</Text>
                    </Descriptions.Item>
                    {document.fileName && (
                      <Descriptions.Item label="File Name">
                        <Text>{String(document.fileName)}</Text>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Required">
                      {document.isRequired ? (
                        <Tag color="red">Required</Tag>
                      ) : (
                        <Tag>Optional</Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Completion Status */}
        <Card>
          <Alert
            message="Ready to Submit"
            description="All required information has been collected. Click 'Submit' to create the group and member accounts."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </Card>
      </Space>
    </div>
  );
};

export default GroupReviewForm;
